import { ref } from '@vue/composition-api';

import { useMessages } from '@/base/app';
import {
  BaseDialogFullScreen,
  BaseDialogFullScreenValue,
} from '@/base/app/components/molecules/BaseDialogFullScreenComposable';
import { clearDialogQuery } from '@/base/app/utils/DialogQueryUtils';
import { CourseDisplayName, CourseId, GroupId } from '@/base/domains';
import { isFailed } from '@/base/usecases';
import { assertIsDefined } from '@/utils/Asserts';
import { useRoute, useRouter } from '@/utils/VueUtils';

import { GroupCourseProgresses } from '../../../domains';
import { useGetGroupCourseProgresses } from '../../../usecases';

function useDialogFullScreen() {
  const dialogFullScreen = ref<BaseDialogFullScreen>();
  const dialog = ref<BaseDialogFullScreenValue>({ display: false });
  function opened() {
    return dialog.value.display;
  }
  function info(msg: string, ok?: () => void) {
    assertIsDefined(dialogFullScreen.value);
    dialogFullScreen.value.showDialog(msg, ok);
  }
  return { dialogFullScreen, dialog, opened, info };
}

export function useUserUsageTraineeProgressDialog() {
  const msgs = useMessages({ prefix: 'report.organisms.userUsageTraineeProgressDialog' });
  const route = useRoute();
  const router = useRouter();
  const { dialogFullScreen, dialog, opened, info } = useDialogFullScreen();

  const reports = ref<GroupCourseProgresses[]>([]);
  const courseName = ref<CourseDisplayName>('');
  const loading = ref(false);

  const getReports = useGetGroupCourseProgresses();
  async function fetch(groupId: GroupId, courseId: CourseId) {
    loading.value = true;
    const res = await getReports.execute({ groupId, courseId });
    loading.value = false;
    if (isFailed(res)) return false;
    reports.value = res.reports;
    courseName.value = res.courseName;
    return true;
  }

  function close() {
    dialog.value = { display: false };
    reports.value = [];
    courseName.value = '';
    const to = clearDialogQuery(route, '#userUsageTraineeCourseTable');
    if (to) router.replace(to);
  }

  async function open(groupId: GroupId, courseId: CourseId) {
    dialog.value = { display: true };
    const ret = await fetch(groupId, courseId);
    if (!ret) info(msgs.of('noData').value, close);
  }

  return {
    dialogFullScreen,
    dialog,
    loading,
    items: reports,
    title: msgs.of('userUsageProgress'),
    subtitle: courseName,
    close,
    open,
    opened,
  };
}

export type UserUsageTraineeProgressDialog = ReturnType<typeof useUserUsageTraineeProgressDialog>;
